import React from 'react';
import {
  X, MapPin, Briefcase, Award, Building2, Clock,
  Users, Bell, Globe, Lock, CheckCircle
} from 'lucide-react';

interface ProfileCardProps {
  profile: any;
  photoUrl: string | null;
  connectionsCount: number;
  followersCount: number;
  onClose: () => void;
  theme: 'light' | 'dark';
}

export default function ProfileCard({ profile, photoUrl, connectionsCount, followersCount, onClose, theme }: ProfileCardProps) {
  const isDark = theme === 'dark';

  const availabilityConfig = {
    available: { label: 'Available', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
    on_project: { label: 'On Project', color: 'bg-amber-100 text-amber-700 border-amber-200' },
    unavailable: { label: 'Unavailable', color: 'bg-red-100 text-red-700 border-red-200' },
  };

  const avail = availabilityConfig[profile.availability_status as keyof typeof availabilityConfig] || availabilityConfig.available;

  const modules = profile.workday_modules
    ? profile.workday_modules.split(',').map((m: string) => m.trim()).filter(Boolean)
    : [];

  const certs = profile.certifications
    ? profile.certifications.split(',').map((c: string) => c.trim()).filter(Boolean)
    : [];

  const initials = `${profile.first_name?.[0] || ''}${profile.last_name?.[0] || ''}`.toUpperCase();

  const visibilityLabel = {
    public: { icon: Globe, label: 'Public Profile' },
    private: { icon: Lock, label: 'Private Profile' },
    network: { icon: Users, label: 'Network Visible' },
  }[profile.profile_visibility || 'public'];

  const VisIcon = visibilityLabel?.icon || Globe;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div
        className={`relative w-full max-w-md rounded-2xl shadow-2xl overflow-hidden border ${
          isDark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="h-24 bg-gradient-to-r from-[#40E0D0] to-[#2bbfb0] relative">
          <button
            onClick={onClose}
            className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/20 hover:bg-black/40 flex items-center justify-center text-white transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="absolute -bottom-10 left-6">
            {photoUrl ? (
              <img
                src={photoUrl}
                alt={`${profile.first_name} ${profile.last_name}`}
                className="w-20 h-20 rounded-full object-cover border-4 border-white shadow-lg"
                style={{
                  objectPosition: `${profile.photo_position_x ?? 50}% ${profile.photo_position_y ?? 50}%`
                }}
              />
            ) : (
              <div className="w-20 h-20 rounded-full border-4 border-white shadow-lg bg-[#40E0D0] flex items-center justify-center">
                <span className="text-white text-2xl font-bold">{initials || '?'}</span>
              </div>
            )}
          </div>
        </div>

        <div className="pt-14 px-6 pb-6">
          <div className="flex items-start justify-between mb-1">
            <div>
              <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {profile.first_name} {profile.last_name}
              </h2>
              {profile.title && (
                <p className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                  {profile.title}
                </p>
              )}
            </div>
            <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${avail.color}`}>
              {avail.label}
            </span>
          </div>

          <div className={`flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            {profile.company && (
              <span className="flex items-center gap-1">
                <Building2 className="w-3.5 h-3.5" />
                {profile.company}
              </span>
            )}
            {profile.country_region && (
              <span className="flex items-center gap-1">
                <MapPin className="w-3.5 h-3.5" />
                {profile.country_region}
              </span>
            )}
            {profile.experience_years && (
              <span className="flex items-center gap-1">
                <Briefcase className="w-3.5 h-3.5" />
                {profile.experience_years} yrs exp.
              </span>
            )}
          </div>

          <div className={`flex items-center gap-4 mt-3 pt-3 border-t text-sm ${isDark ? 'border-gray-700 text-gray-400' : 'border-gray-100 text-gray-500'}`}>
            <span className="flex items-center gap-1.5">
              <Users className="w-3.5 h-3.5" />
              <strong className={isDark ? 'text-white' : 'text-gray-900'}>{connectionsCount}</strong> connections
            </span>
            <span className="flex items-center gap-1.5">
              <Bell className="w-3.5 h-3.5" />
              <strong className={isDark ? 'text-white' : 'text-gray-900'}>{followersCount}</strong> followers
            </span>
          </div>

          {modules.length > 0 && (
            <div className="mt-4">
              <p className={`text-xs font-semibold uppercase tracking-wide mb-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                Workday Modules
              </p>
              <div className="flex flex-wrap gap-1.5">
                {modules.slice(0, 6).map((mod: string, i: number) => (
                  <span
                    key={i}
                    className={`text-xs px-2.5 py-1 rounded-full font-medium border ${
                      isDark
                        ? 'bg-[#40E0D0]/10 text-[#40E0D0] border-[#40E0D0]/30'
                        : 'bg-[#40E0D0]/10 text-[#1a9e90] border-[#40E0D0]/40'
                    }`}
                  >
                    {mod}
                  </span>
                ))}
                {modules.length > 6 && (
                  <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    +{modules.length - 6} more
                  </span>
                )}
              </div>
            </div>
          )}

          {certs.length > 0 && (
            <div className="mt-3">
              <p className={`text-xs font-semibold uppercase tracking-wide mb-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                Certifications
              </p>
              <div className="flex flex-wrap gap-1.5">
                {certs.slice(0, 4).map((cert: string, i: number) => (
                  <span
                    key={i}
                    className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-medium bg-amber-50 text-amber-700 border border-amber-200"
                  >
                    <Award className="w-3 h-3" />
                    {cert}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className={`mt-4 pt-3 border-t flex items-center justify-between text-xs ${isDark ? 'border-gray-700 text-gray-500' : 'border-gray-100 text-gray-400'}`}>
            <span className="flex items-center gap-1.5">
              <VisIcon className="w-3.5 h-3.5" />
              {visibilityLabel?.label || 'Public Profile'}
            </span>
            {profile.profile_complete && (
              <span className="flex items-center gap-1 text-emerald-600">
                <CheckCircle className="w-3.5 h-3.5" />
                Profile complete
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
